import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const CategoryFormModal = ({ onClose, onSubmit, editingCategory, categories }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategoryId: null,
    level: 0,
    displayOrder: 0,
    materialAttributes: false
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [parentCategory, setParentCategory] = useState(null);

  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory?.name || '',
        description: editingCategory?.description || '',
        parentCategoryId: editingCategory?.parent_category_id || null,
        level: editingCategory?.level || 0,
        displayOrder: editingCategory?.display_order || 0,
        materialAttributes: editingCategory?.material_attributes || false
      });

      if (editingCategory?.parent_category_id) {
        const parent = categories?.find(c => c?.id === editingCategory?.parent_category_id);
        setParentCategory(parent);
      }
    }
  }, [editingCategory, categories]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleParentChange = (parentId) => {
    const parent = categories?.find(c => c?.id === parentId);
    setParentCategory(parent);
    
    const newLevel = parent ? parent?.level + 1 : 0;
    
    if (newLevel > 5) {
      setErrors({ ...errors, parentCategoryId: 'Maximum nesting level (5) reached' });
      return;
    }

    setFormData({
      ...formData,
      parentCategoryId: parentId || null,
      level: newLevel
    });

    if (errors?.parentCategoryId) {
      setErrors({ ...errors, parentCategoryId: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Category name is required';
    if (formData?.level > 5) newErrors.level = 'Maximum nesting level is 5';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    await onSubmit(formData);
    setSubmitting(false);
    onClose();
  };

  // Get available parent categories (exclude current category and its children if editing)
  const getAvailableParents = () => {
    if (!editingCategory) {
      return categories?.filter(c => c?.level < 5) || [];
    }

    // Exclude current category and its descendants
    const excludeIds = new Set([editingCategory?.id]);
    const findDescendants = (parentId) => {
      categories?.forEach(cat => {
        if (cat?.parent_category_id === parentId) {
          excludeIds?.add(cat?.id);
          findDescendants(cat?.id);
        }
      });
    };
    findDescendants(editingCategory?.id);

    return categories?.filter(c => !excludeIds?.has(c?.id) && c?.level < 5) || [];
  };

  const availableParents = getAvailableParents();

  const parentOptions = [
    { value: '', label: 'None (Root Category)' },
    ...availableParents?.map(cat => ({
      value: cat?.id,
      label: `${'  '?.repeat(cat?.level)}${cat?.name} (Level ${cat?.level})`
    }))
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Layers" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {editingCategory ? 'Update category information' : 'Add a new material category'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors duration-250"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-4 space-y-4">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category Name <span className="text-destructive">*</span>
              </label>
              <Input
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder="Enter category name"
                error={errors?.name}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Enter category description"
                rows={3}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Parent Category */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Parent Category
              </label>
              <Select
                value={formData?.parentCategoryId || ''}
                onChange={(value) => handleParentChange(value)}
                options={parentOptions}
                error={errors?.parentCategoryId}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Select a parent category to create a subcategory (max 5 levels)
              </p>
            </div>

            {/* Level Display */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Category Level:</span>
                <span className="text-sm font-semibold text-foreground">Level {formData?.level}</span>
              </div>
              {parentCategory && (
                <div className="flex items-center gap-2 mt-2">
                  <Icon name="ArrowRight" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-xs text-muted-foreground">
                    Will be nested under: {parentCategory?.name}
                  </span>
                </div>
              )}
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Display Order
              </label>
              <Input
                type="number"
                value={formData?.displayOrder}
                onChange={(e) => handleInputChange('displayOrder', parseInt(e?.target?.value) || 0)}
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower numbers appear first in the list
              </p>
            </div>

            {/* Material Attributes Checkbox */}
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <Checkbox
                id="material-attributes"
                checked={formData?.materialAttributes}
                onChange={(checked) => handleInputChange('materialAttributes', checked)}
                label="Material Attributes"
                description="This category references the level above it in the hierarchy"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  <span>{editingCategory ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <span>{editingCategory ? 'Update Category' : 'Create Category'}</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;
